// =============================================================================
// File: frontend/src/components/SimulationModal.jsx
// What-If Simulation Input Modal — 5 categories
// Patent claims exercised: P-011 C2, C16
// =============================================================================

import React, { useState } from 'react';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import CostChip from './CostChip';  // #77 twin_simulation = 2 credits

const CATEGORY_FIELDS = {
  diet_change: {
    title: 'Diet Change Simulation',
    fields: [
      { key: 'diet_type', label: 'New Diet Type', type: 'select',
        options: ['keto', 'mediterranean', 'vegan', 'paleo', 'low_carb', 'high_protein', 'dash', 'custom'] },
      { key: 'calorie_target', label: 'Daily Calorie Target', type: 'number', placeholder: 'e.g., 2000' },
      { key: 'description', label: 'Additional Details', type: 'textarea', placeholder: 'Describe the change...' },
    ],
  },
  medication_change: {
    title: 'Medication Change Simulation',
    fields: [
      { key: 'medication_name', label: 'Medication', type: 'text', placeholder: 'e.g., Metformin' },
      { key: 'change_type', label: 'Change Type', type: 'select',
        options: ['start', 'stop', 'increase_dose', 'decrease_dose', 'switch'] },
      { key: 'new_dosage', label: 'New Dosage', type: 'text', placeholder: 'e.g., 500mg twice daily' },
      { key: 'description', label: 'Additional Details', type: 'textarea', placeholder: 'Notes...' },
    ],
  },
  exercise_change: {
    title: 'Exercise Change Simulation',
    fields: [
      { key: 'exercise_type', label: 'Exercise Type', type: 'select',
        options: ['cardio', 'strength', 'hiit', 'yoga', 'swimming', 'cycling', 'running', 'walking'] },
      { key: 'frequency', label: 'Sessions per Week', type: 'number', placeholder: 'e.g., 4' },
      { key: 'duration_min', label: 'Duration (minutes)', type: 'number', placeholder: 'e.g., 45' },
      { key: 'intensity', label: 'Intensity', type: 'select', options: ['low', 'moderate', 'high', 'very_high'] },
    ],
  },
  supplement_change: {
    title: 'Supplement Change Simulation',
    fields: [
      { key: 'supplement_name', label: 'Supplement', type: 'text', placeholder: 'e.g., Vitamin D3' },
      { key: 'change_type', label: 'Change Type', type: 'select', options: ['start', 'stop', 'increase_dose'] },
      { key: 'dosage', label: 'Dosage', type: 'text', placeholder: 'e.g., 5000 IU daily' },
    ],
  },
  schedule_change: {
    title: 'Schedule Change Simulation',
    fields: [
      { key: 'change_description', label: 'What are you changing?', type: 'textarea',
        placeholder: 'e.g., Moving dinner from 7pm to 5pm, taking medications earlier...' },
      { key: 'new_wake_time', label: 'New Wake Time', type: 'text', placeholder: 'e.g., 06:00' },
      { key: 'new_sleep_time', label: 'New Sleep Time', type: 'text', placeholder: 'e.g., 22:00' },
    ],
  },
};

export default function SimulationModal({ category, onRun, onClose, running }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const config = CATEGORY_FIELDS[category];
  const [formData, setFormData] = useState({});
  const [horizon, setHorizon] = useState(14);

  if (!config) return null;

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onRun(category, formData, horizon);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content dt-simModal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{config.title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body dt-simForm">
          {config.fields.map(field => (
            <div key={field.key} className="dt-simForm-field">
              <label>{field.label}</label>
              {field.type === 'select' ? (
                <select
                  value={formData[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  required
                >
                  <option value="">Select...</option>
                  {field.options.map(opt => (
                    <option key={opt} value={opt}>
                      {opt.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={formData[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  rows={3}
                />
              ) : (
                <input
                  type={field.type}
                  value={formData[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                />
              )}
            </div>
          ))}

          <div className="dt-simForm-field">
            <label>Prediction Horizon</label>
            <div className="dt-horizonBtns">
              {[7, 14, 30].map(d => (
                <button
                  key={d}
                  type="button"
                  className={`dt-horizonBtn ${horizon === d ? 'active' : ''}`}
                  onClick={() => setHorizon(d)}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>

          <div className="dt-simForm-disclaimer">
            <em>This simulation uses AI to predict potential outcomes based on your health data.
            It is NOT medical advice. Always consult your healthcare provider before making changes.</em>
          </div>

          {running && (
            <div className="dt-simModal-running">
              <div className="dt-spinner"></div>
              <p>Running simulation — analyzing your health data...</p>
              <p className="dt-aiOverlay-sub">This may take 15-30 seconds</p>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={running}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={running}>
              {running ? 'Running Simulation...' : <>Run Simulation <CostChip apiType="twin_simulation" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
